import axios from "axios";
import { downloadBase64Pdf } from "./chatPdfApi";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

/**
 * Map a single canvas widget (frontend state) to the backend report data
 * contract. This is the one place that couples the canvas widget shape to the
 * PDF payload, so the Reports canvas can stay decoupled from the generator.
 * Adjust the field reads here to match the real canvas widget state.
 */
function mapWidget(widget) {
  if (!widget || typeof widget !== "object") return null;
  return {
    id: widget.id,
    type: widget.type,
    label: widget.label || widget.title || "",
    channel: widget.channel,
    unit: widget.unit,
    // KPI
    value: widget.value,
    delta: widget.delta,
    deltaDirection: widget.deltaDirection,
    // Time-series
    series: widget.series,
    points: widget.points,
    // Breakdown
    items: widget.items,
    // Table
    columns: widget.columns,
    rows: widget.rows,
  };
}

/**
 * Build the request body from the canvas snapshot. `canvasWidgets` is the
 * ordered widget list the user placed on the main canvas, each carrying the
 * data it already rendered (the snapshot) so the PDF matches what was on screen.
 */
export function buildReportPayload(canvasWidgets, meta = {}) {
  const widgets = (Array.isArray(canvasWidgets) ? canvasWidgets : [])
    .map(mapWidget)
    .filter(Boolean);

  return {
    report: {
      title: meta.title || "MarkBase Report",
      subtitle: meta.subtitle || "",
      companyName: meta.companyName || "",
      dateRange: meta.dateRange || null,
      generatedAt: meta.generatedAt || new Date().toISOString(),
    },
    widgets,
  };
}

/**
 * POST a report payload to the backend and trigger a browser download of the
 * returned PDF. Reuses the existing base64 -> Blob download helper.
 * @returns {Promise<{ filename: string }>}
 */
export async function downloadReportPdf(payload) {
  const { data } = await axios.post(`${API_BASE}/reports/generate-pdf`, payload);
  const attachment = data && data.attachment;
  if (!attachment || !attachment.data) {
    throw new Error("Report generation returned no PDF");
  }
  const filename = attachment.filename || "report.pdf";
  downloadBase64Pdf(attachment.data, filename);
  return { filename };
}

/**
 * Convenience: build the payload from canvas state and download in one call.
 */
export async function downloadCanvasReport(canvasWidgets, meta = {}) {
  const payload = buildReportPayload(canvasWidgets, meta);
  return downloadReportPdf(payload);
}
